import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import { useChatStreamStatus } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatStreamStatusContext'
import {
  buildCancelStagedActionMessage,
  buildConfirmStagedActionMessage,
} from './stagedActionMessages'

const Card = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(16)};
  background: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(16)};
`

const Title = styled(Heading)`
  display: block;
  margin-bottom: ${rem(8)};
`

const Body = styled(BodyText)`
  display: block;
  margin-bottom: ${rem(16)};
`

const Actions = styled.div`
  display: flex;
  gap: ${rem(8)};
  flex-wrap: wrap;
`

const HistoryToggle = styled.div`
  margin-top: ${rem(12)};
`

export interface StagedActionConfirmationPromptProps {
  pendingActionId: string
  title: string
  body: string
  confirmButtonLabel: string
  cancelButtonLabel: string
  selectedAction?: 'confirm' | 'cancel'
  isHistorical?: boolean
  onSendMessage: (message: string) => void
}

function shouldCollapseHistory({
  isHistorical,
  isExpanded,
  selectedAction,
}: {
  isHistorical: boolean
  isExpanded: boolean
  selectedAction?: 'confirm' | 'cancel'
}): boolean {
  return isHistorical && !isExpanded && !selectedAction
}

function StagedActionConfirmationPrompt({
  pendingActionId,
  title,
  body,
  confirmButtonLabel,
  cancelButtonLabel,
  selectedAction,
  isHistorical = false,
  onSendMessage,
}: StagedActionConfirmationPromptProps) {
  const intl = useIntl()
  const { isStreaming } = useChatStreamStatus()
  const [isExpanded, setIsExpanded] = useState(() => !(isHistorical && !selectedAction))
  const labels = {
    showOptionsLabel: intl.formatMessage({
      id: 'h96BUzfKENlUYTN7sOZH',
      defaultMessage: 'Show options',
      description: 'Locked-history button label that expands a staged action confirmation prompt',
    }),
    hideOptionsLabel: intl.formatMessage({
      id: 'J2MXXfRoEIIDlbIAo0nU',
      defaultMessage: 'Hide options',
      description: 'Locked-history button label that collapses a staged action confirmation prompt',
    }),
  }

  useEffect(() => {
    setIsExpanded(!(isHistorical && !selectedAction))
  }, [isHistorical, selectedAction])

  if (selectedAction) {
    return (
      <Actions>
        <TextButton
          kind={selectedAction === 'confirm' ? 'primary' : 'secondary'}
          size="medium"
          disabled
        >
          {selectedAction === 'confirm' ? confirmButtonLabel : cancelButtonLabel}
        </TextButton>
      </Actions>
    )
  }

  if (shouldCollapseHistory({ isHistorical, isExpanded, selectedAction })) {
    return (
      <TextButton
        kind="tertiary"
        size="small"
        onClick={() => setIsExpanded(true)}
      >
        {labels.showOptionsLabel}
      </TextButton>
    )
  }

  return (
    <Card>
      <Title variant="heading6" colour="neutral-one">
        {title}
      </Title>
      <Body variant="medium" colour="neutral-one">
        {body}
      </Body>

      <Actions>
        <TextButton
          kind="primary"
          size="medium"
          disabled={isHistorical || isStreaming}
          onClick={() => onSendMessage(buildConfirmStagedActionMessage(pendingActionId))}
        >
          {confirmButtonLabel}
        </TextButton>
        <TextButton
          kind="secondary"
          size="medium"
          disabled={isHistorical || isStreaming}
          onClick={() => onSendMessage(buildCancelStagedActionMessage(pendingActionId))}
        >
          {cancelButtonLabel}
        </TextButton>
      </Actions>

      {isHistorical && (
        <HistoryToggle>
          <TextButton
            kind="tertiary"
            size="small"
            onClick={() => setIsExpanded(false)}
          >
            {labels.hideOptionsLabel}
          </TextButton>
        </HistoryToggle>
      )}
    </Card>
  )
}

export default StagedActionConfirmationPrompt
